import React, { useState, useEffect } from 'react';
import { Table, Paper, Button, TableContainer, TableRow, TableBody, TableHead, Pagination, CircularProgress } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import axios from "axios";
import API_GATEWAY_URL from '../../config';
import { Confirm } from 'react-admin'; // Assuming you have a Confirm component

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.MuiTableCell-head`]: {
    backgroundColor: 'rgb(150, 150, 150)',
    color: theme.palette.common.white,
  },
  [`&.MuiTableCell-body`]: {
    maxWidth: '100px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function formatLastModified(lastModified) {
  if (lastModified) {
    const inputDate = new Date(lastModified);
    const year = inputDate.getFullYear();
    const month = (inputDate.getMonth() + 1).toString().padStart(2, "0");
    const day = inputDate.getDate().toString().padStart(2, "0");
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = months[parseInt(month) - 1];
    const formattedDate = `${monthName} ${day}, ${year}`;
    return formattedDate;
  }
  return null;
}

function formatList(list) {
  return list.map(item => item.charAt(0).toUpperCase() + item.slice(1)).join(', ');
}

function DataRecords() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [dataRecords, setDataRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isFetching = false;
    const fetchData = () => {
      if (isFetching) return;
      isFetching = true;

      const jwt_token = localStorage.getItem('palm_care_token');
      const apiUrl = `${API_GATEWAY_URL}/blocks/detail`;
      const headers = { Authorization: jwt_token };
      axios.get(apiUrl, { headers, params: { page: currentPage }})
        .then((response) => {
          response.data.blocks_detail.forEach((block) => { block.isDeleting = false; });
          setDataRecords(response.data.blocks_detail);
          if (response.data.total_pages !== pageCount) setPageCount(response.data.total_pages);
          setIsLoading(false);
          isFetching = false;
        })
        .catch((error) => { 
          console.error("Error fetching block data:", error); 
          setIsLoading(false);
          isFetching = false;
        });
    };

    fetchData();

    const fetchDataInterval = setInterval(fetchData, 10 * 1000);
    return () => clearInterval(fetchDataInterval);
  }, [currentPage]);

  const handlePageClick = (event, selectedPage) => setCurrentPage(selectedPage);

  const handleDelete = (row) => {
    const jwt_token = localStorage.getItem('palm_care_token');
    const apiUrl = `${API_GATEWAY_URL}/block`;
    const headers = { Authorization: jwt_token };
    axios.delete(apiUrl, { headers, params: { container: row.container, estate: row.estate, block: row.block }})
      .then(() => { toast.success(`Successfully Removed Data - ${row.block}`); })
      .catch((error) => { console.error("Error:", error); });
  };

  const handleConfirm = () => {
    if (deleteRow) {
      handleDelete(deleteRow);
      setDeleteRow(null);
      setOpen(false);
    }
  };

  return (
    <div className="data__records__container">
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Last Modified</StyledTableCell>
              <StyledTableCell>Acquisition Date</StyledTableCell>
              <StyledTableCell>Estate</StyledTableCell>
              <StyledTableCell>Block</StyledTableCell>
              <StyledTableCell>Raster</StyledTableCell>
              <StyledTableCell>Vector</StyledTableCell>
              <StyledTableCell>Size</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <StyledTableCell colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                  <CircularProgress />
                </StyledTableCell>
              </TableRow>
            ) : dataRecords.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={8} style={{ textAlign: 'center' }}>No Data Available</StyledTableCell>
              </TableRow>
            ) : (
              dataRecords.map((row) => (
                <StyledTableRow key={row.id}>
                  <StyledTableCell>{formatLastModified(row.lastModified)}</StyledTableCell>
                  <StyledTableCell>{formatLastModified(row.acquisition_date)}</StyledTableCell>
                  <StyledTableCell>{row.estate}</StyledTableCell>
                  <StyledTableCell>{row.block}</StyledTableCell>
                  <StyledTableCell>{formatList(row.raster)}</StyledTableCell>
                  <StyledTableCell>{formatList(row.vector)}</StyledTableCell>
                  <StyledTableCell>{formatBytes(row.size)}</StyledTableCell>
                  <StyledTableCell style={{ maxWidth: 'none', overflow: 'visible' }}>
                    <Button
                      disabled={!(row.raster && row.raster.length > 0 && row.vector && row.vector.includes('boundary'))}
                      style={{
                        backgroundColor: (row.raster && row.raster.length > 0 && row.vector && row.vector.includes('boundary')) ? "#002D62" : "#ccc",
                        color: "white",
                        fontWeight: 550,
                        width: "120px",
                        marginRight: "8px"
                      }}
                      onClick={() => {
                        localStorage.setItem('tools_selected_block_name', JSON.stringify({ 
                          estate: row.estate, 
                          block: row.block,
                          container: row.container,
                          target_email: row.target_email
                        }));
                        window.dispatchEvent(new CustomEvent('changeTab', { detail: 'Tools' }));
                      }}
                    >
                      Submit Job
                    </Button>
                    <Button
                      style={{ backgroundColor: "rgb(220,100,100)", color: "white", fontWeight: 550, width: "100px" }}
                      onClick={() => { setOpen(true); setDeleteRow(row); }}
                    >
                      Delete
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        count={pageCount}
        color="primary"
        onChange={handlePageClick}
        style={{ display: 'flex', justifyContent: 'center', margin: '20px' }}
      />
      <Confirm
        isOpen={open}
        title={`Delete Item`}
        confirm={"Confirm"}
        cancel={"Cancel"}
        content="Are you sure you want to delete this item?"
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}

export default DataRecords;
